import { PermissionKey, Role, useAuth } from '@multiplatform-apps/service-trips';
import type { IndexPermissions } from '@multiplatform-apps/service-trips';
import { useCallback } from 'react';

import { isRoutePermissionOverridden } from './use-action-permissions';

type UserRoles = ReturnType<typeof useAuth>['roles'];
type GuardianPermission = Exclude<PermissionKey, 'tripsPage' | 'profilePage'>;

/** Maps PermissionKey → V2 IndexPermissions boolean field */
const PERMISSION_KEY_TO_INDEX: Record<PermissionKey, keyof IndexPermissions | null> = {
  tripsPage: 'canSeeTripsNavItem',
  passengersPage: 'canSeePassengersNavItem',
  usersPage: 'canSeeUsersNavItem',
  profilePage: null, // Always allowed for authenticated users
  absencePage: 'canSeeGeneralAbsenceMenuItem',
  notificationsPage: 'canSeeNotificationsMenuItem',
  institutionsPage: 'canSeeTravelAddressesNavItem',
  adminPage: 'canSeeGeneralAbsenceMenuItem',
};

const isGuardianOnly = (roles: UserRoles): boolean =>
  roles.length === 1 && roles[0] === Role.Guardian;

const GUARDIAN_BLOCKED_PERMISSIONS: ReadonlySet<GuardianPermission> = new Set([
  'passengersPage',
  'usersPage',
  'absencePage',
  'notificationsPage',
  'institutionsPage',
  'adminPage',
]);

const resolveGuardianPermission = (permissionKey: PermissionKey): boolean | null => {
  if (permissionKey === 'tripsPage' || permissionKey === 'profilePage') return true;
  if (GUARDIAN_BLOCKED_PERMISSIONS.has(permissionKey)) return false;
  return false;
};

const computeHasRootRoutePermission = (
  indexPermissions: IndexPermissions | undefined,
  roles: UserRoles,
  permissionKey: PermissionKey,
): boolean => {
  // Dev override — bypass all checks when explicitly overridden
  if (isRoutePermissionOverridden(permissionKey)) return true;

  if (isGuardianOnly(roles)) {
    const guardianPermission = resolveGuardianPermission(permissionKey);
    if (guardianPermission !== null) return guardianPermission;
  }

  const indexKey = PERMISSION_KEY_TO_INDEX[permissionKey];
  // profilePage (null-mapped) is always allowed for authenticated users
  if (indexKey === null) return true;

  return indexPermissions?.[indexKey] ?? false;
};

/** Maps sub-route path names to their corresponding V2 IndexPermissions boolean field */
const SUB_PATH_TO_INDEX: Record<string, keyof IndexPermissions> = {
  'user-search': 'canSearchUsers',
  'passenger-search': 'canSearchPassengers',
  'trip-search': 'canSearchTrips',
};

const computeHasSubRoutePermission = (
  indexPermissions: IndexPermissions | undefined,
  roles: UserRoles,
  permissionKey: PermissionKey,
  allowedPaths: string[] | undefined,
): boolean => {
  if (isGuardianOnly(roles)) {
    const guardianPermission = resolveGuardianPermission(permissionKey);
    if (guardianPermission === false) return false;
  }

  if (indexPermissions && allowedPaths) {
    return allowedPaths.some((path) => {
      const permKey = SUB_PATH_TO_INDEX[path];
      return permKey ? indexPermissions[permKey] : false;
    });
  }

  return false;
};

export const usePermissions = () => {
  const { indexPermissions, roles } = useAuth();

  const hasRootRoutePermission = useCallback(
    (permissionKey: PermissionKey) => computeHasRootRoutePermission(indexPermissions, roles, permissionKey),
    [indexPermissions, roles]
  );

  const hasSubRoutePermission = useCallback(
    (permissionKey: PermissionKey, allowedPaths: string[] | undefined) =>
      computeHasSubRoutePermission(indexPermissions, roles, permissionKey, allowedPaths),
    [indexPermissions, roles]
  );

  return { hasRootRoutePermission, hasSubRoutePermission };
};

/**
 * V2 index permissions — explicit boolean fields from GET /v2/index (ENB-1610).
 * Returns undefined until permissions are loaded.
 */
export const useIndexPermissions = (): IndexPermissions | undefined => {
  const { indexPermissions } = useAuth();
  return indexPermissions;
};
